import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Autocomplete from '@material-ui/lab/Autocomplete'

import { Button, Icon, makeStyles, TextField, Typography } from '@material-ui/core'

import { getHouseholds } from '../../actions/households/households.actions'

import { debounce } from 'lodash'
import clsx from 'clsx'
import { states } from '../../services/helpers'

const useStyles = makeStyles((theme) => ({
	householdSearch: {
		'& .MuiAutocomplete-inputRoot': {
			paddingTop: '8px'
		}
	},
	clientWrapper: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: 16,
		borderRadius: 6,
		background: '#F9F9F9'
	},
	userIcon: {
		width: '16px',
		height: '18px',
		'&:before': {
			color: '#777679'
		}
	}
}))

function useQuery() {
	return new URLSearchParams(useLocation().search)
}

const HouseholdAutocomplete = () => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const query = useQuery()
	const facility = useSelector((state) => state.specific.facilities.selected)
	const households = useSelector((state) => state.specific.households.households)
	const household = useSelector((state) => state.specific.households.selected)

	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = useState('')

	const delayedSetSearch = useRef(debounce((t) => setSearch(t), 750)).current

	const loading = open && households.length === 0

	const updateSearch = (value) => delayedSetSearch(value)

	useEffect(() => {
		if ((household && !query.get('household')) || (household && query.get('household') !== household._id)) {
			clearHousehold()
		}
	}, [])

	useEffect(() => {
		if (search && facility) {
			dispatch(getHouseholds(facility.market_id, encodeURIComponent(search), encodeURIComponent('Contact')))
		} else {
			clearHouseholds()
		}
	}, [search])

	const setHousehold = (household) => {
		dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: household })
	}

	useEffect(() => {
		if (!open) {
			clearHouseholds()
			setSearch('')
		}
	}, [open])

	const clearHouseholds = () => {
		dispatch({ type: 'GET_HOUSEHOLDS_SUCCESS', payload: [] })
	}

	const clearHousehold = () => {
		dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: null })
	}

	const renderAddress = () => {
		let { contact } = household
		let { address } = contact
		let { address_line_1, address_line_2, city, state, zip_code } = address

		state = states.find((s) => s.id.toString() === state)

		return (
			<div>
				<Typography variant="body2">{household.account.full_name || 'No name'}</Typography>
				{contact ? (
					<div>
						<Typography variant="body2">{address_line_1 || 'No address'}</Typography>
						{address_line_2 ? <Typography variant="body2">{address_line_2}</Typography> : null}
						<Typography variant="body2">{`${city ? city : ''}${state ? `, ${state.name}` : ''}${
							zip_code ? ` ${zip_code}` : ''
						}`}</Typography>
					</div>
				) : null}
			</div>
		)
	}

	return !household ? (
		<Autocomplete
			id="household-select"
			getOptionLabel={(option) =>
				typeof option === 'string' ? option?.account?.full_name : option.account.full_name || 'No Name'
			}
			filterOptions={(x) => x}
			options={households && households.length ? households.filter((h) => h.account) : []}
			className={classes.householdSearch}
			autoComplete
			noOptionsText="No other contacts"
			loading={loading}
			includeInputInList
			disableOpenOnFocus
			open={open}
			onOpen={() => {
				setOpen(true)
			}}
			onClose={() => {
				setOpen(false)
			}}
			onChange={(event, newValue) => {
				setHousehold(newValue)
			}}
			renderOption={(option) => (
				<React.Fragment>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<Typography variant="body2">{option?.account?.full_name}</Typography>
						<Typography style={{ fontSize: '10px' }}>{option?.account?.email}</Typography>
					</div>
				</React.Fragment>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Search existing clients"
					variant="filled"
					fullWidth
					onChange={(e) => updateSearch(e.target.value)}
				/>
			)}
		/>
	) : (
		<div className={classes.clientWrapper}>
			<div style={{ display: 'flex', alignItems: 'flex-start' }}>
				<Icon
					className={clsx([classes.userIcon, 'far fa-user'])}
					style={{
						fontSize: '16px',
						padding: '2px',
						marginTop: '3px',
						marginRight: '24px'
					}}
				/>
				<div>{household.account ? renderAddress() : null}</div>
			</div>
			<div>
				<Button color="secondary" variant="outlined" onClick={() => clearHousehold()}>
					Change Client
				</Button>
			</div>
		</div>
	)
}

export default HouseholdAutocomplete
